// ============================================================
// kinds - what each kind of asset stores, one row each.
//
// Adding a kind is adding a row (the ACTION_CATALOG and SETTINGS pattern).
// These are the kinds kept as TEXT; the kinds kept as files (images,
// handouts) are rows of their own in `fileKinds.ts`, and `FACTS` holds what
// every kind shows. A version stores the SAME file a GM would export - `deathwatch-maps`,
// `deathwatch-assets`, `deathwatch-scene`, a character's sheet - so an export
// is the stored text as it is, and an upload is a file the readers already
// know. Pure (a scene's read mints its ids from the roster's clock, as every
// scene import does).
// ============================================================
import { readMapFile, toMapFile, MAP_FILE_KIND } from '../battlefield/mapFile';
import { readAssetFile, toAssetFile, ASSET_FILE_KIND } from '../battlefield/assetFile';
import { emptyTerrain, type Terrain } from '../battlefield/presets';
import type { SurfaceDef } from '../battlefield/zones';
import type { Material } from '../battlefield/voxels';
import { readSceneFile, sceneFromFile, toSceneFile, SCENE_FILE_KIND } from '../scenes/sceneFile';
import { emptyScene } from '../scenes/park';
import { defaultMint } from '../scenes/mint';
import type { ParkedScene } from '../scenes/types';
import type { Character } from '../character/types';
import { emptyCharacter } from '../character/factories';
import { normalizeCharacter } from '../character/normalize';
import { withImportableCharacterIds } from '../importIds';
import { combatantFromCharacter } from '../roster/factories';
import { projectDefender } from '../roster/projection';
import { figureFor } from '../plate/figureFor';
import { isFileKind, type AssetKind, type AssetRow, type DocKind } from './types';
import { FILE_ROWS, type KindFacts } from './fileKinds';
import { swatchShot, thumbScene, type ThumbShot } from './thumbScene';
import { ARMOURY_ROWS, type ArmouryContent } from './armouryKinds';

interface MapContent {
  name: string;
  terrain: Terrain;
}

interface PaletteContent {
  surfaces: Record<string, SurfaceDef>;
  materials: Record<string, Material>;
}

/** What each kind's content is, decoded. */
export interface ContentOf extends ArmouryContent {
  map: MapContent;
  palette: PaletteContent;
  scene: ParkedScene;
  character: Character;
}

/** An asset open in its editor: the row and its current content, one shape per kind. */
export type Opened = { [K in DocKind]: { kind: K; asset: AssetRow; content: ContentOf[K] } }[DocKind];

type Decoded<T> = { content: T; error?: undefined } | { content?: undefined; error: string };

export interface KindRow<K extends DocKind> extends KindFacts {
  kind: K;
  /** The text a version stores. */
  encode(content: ContentOf[K]): string;
  /** The content back, or a sentence a person can act on. Never throws. */
  decode(text: string): Decoded<ContentOf[K]>;
  /** Is this parsed file one of this kind? How an upload finds its row. */
  claims(data: unknown): boolean;
  /** The name an uploaded file gives its asset; `fallback` is the file's own name. */
  nameOf(content: ContentOf[K], fallback?: string): string;
  /** A new, empty one: what "New map" saves first. `id` names what the content needs named. */
  blank(name: string, id: string): ContentOf[K];
  /** The same content under a new identity, for a duplicate - where an id inside it would collide. */
  fresh?(content: ContentOf[K], id: string): ContentOf[K];
  /** The card's picture, as polygons: a map's establishing shot, a palette's swatches; null keeps the kind's drawing. */
  shot(content: ContentOf[K]): ThumbShot | null;
}

function parse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

const isRecord = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);
const kindIs = (fileKind: string) => (data: unknown) => isRecord(data) && data.kind === fileKind;

const MAP: KindRow<'map'> = {
  kind: 'map',
  label: 'Map',
  plural: 'Maps',
  claims: kindIs(MAP_FILE_KIND),
  nameOf: (content) => content.name,
  blank: (name) => ({ name, terrain: emptyTerrain() }),
  copiesIntoSession: true,
  open: 'Open in Build',
  shot: (content) => thumbScene(content.terrain),
  encode: (content) => JSON.stringify(toMapFile([content])),
  decode: (text) => {
    const read = readMapFile(parse(text));
    return read.error ? { error: read.error } : { content: read.maps[0] };
  },
};

const PALETTE: KindRow<'palette'> = {
  kind: 'palette',
  // "Terrain kit" (plan armoury): "palette" named five unrelated shelves. The kind's id stays `palette`.
  label: 'Terrain kit',
  plural: 'Terrain kits',
  claims: kindIs(ASSET_FILE_KIND),
  // A palette file carries no name of its own: it takes its file's.
  nameOf: (_content, fallback = 'Terrain kit') => fallback,
  blank: () => ({ surfaces: {}, materials: {} }),
  copiesIntoSession: false,
  // Edited in a map's terrain editor, never on its own.
  open: null,
  shot: (content) =>
    swatchShot([...Object.values(content.surfaces), ...Object.values(content.materials)].map((e) => e.colour)),
  encode: (content) => JSON.stringify(toAssetFile(content.surfaces, content.materials)),
  decode: (text) => {
    const read = readAssetFile(parse(text));
    return read.error ? { error: read.error } : { content: { surfaces: read.surfaces, materials: read.materials } };
  },
};

/**
 * A scene is kept WITH its state (the user's ruling, 2026-10-18): where each
 * person stands, their wounds and conditions, the order - the scene file's
 * `includeState`. Reading one mints fresh ids (`sceneFromFile`), so a file's
 * ids never matter and two reads never share a person.
 */
const SCENE: KindRow<'scene'> = {
  kind: 'scene',
  label: 'Scene',
  plural: 'Scenes',
  claims: kindIs(SCENE_FILE_KIND),
  nameOf: (content) => content.record.name,
  blank: (name, id) => ({
    record: emptyScene(id, name, emptyTerrain(), null, 0, 0),
    cast: [],
    terrain: emptyTerrain(),
  }),
  copiesIntoSession: true,
  open: 'Open the scene',
  shot: (content) =>
    thumbScene(
      content.terrain,
      undefined,
      content.cast.flatMap((c) => content.record.marks[c.id] ?? []),
    ),
  encode: (content) => JSON.stringify(toSceneFile(content, { includeState: true, includeNarrative: true })),
  decode: (text) => {
    const read = readSceneFile(parse(text));
    if (!read.scene) return { error: read.error ?? 'That file does not contain a scene.' };
    return { content: sceneFromFile(read.scene, defaultMint, 0, 0) };
  },
};

/**
 * A character is ONE sheet, as its own JSON - the file the session's character
 * library and the browser's both import. It has no `kind` of its own, so a
 * file is claimed by its shape: a name and the characteristics.
 */
const CHARACTER: KindRow<'character'> = {
  kind: 'character',
  label: 'Character',
  plural: 'Characters',
  claims: (data) =>
    isRecord(data) && data.kind === undefined && typeof data.name === 'string' && isRecord(data.characteristics),
  nameOf: (content) => content.name,
  blank: (name, id) => ({ ...emptyCharacter(id), name, createdAt: 0, updatedAt: 0 }),
  // The sheet id becomes `characters/{id}` in a session: a duplicate needs its own.
  fresh: (content, id) => ({ ...content, id }),
  copiesIntoSession: true,
  open: 'Open the sheet',
  shot: (content) => ({
    ground: [],
    faces: [],
    figure: figureFor(projectDefender(combatantFromCharacter(content)), content),
  }),
  encode: (content) => JSON.stringify(content),
  decode: (text) => {
    const data = withImportableCharacterIds(parse(text));
    if (!isRecord(data)) return { error: 'That file does not contain a character.' };
    if (typeof data.name !== 'string' || !data.name.trim()) return { error: 'A character needs a name.' };
    return { content: normalizeCharacter(data) };
  },
};

/** The kinds kept as text, one row each. */
export const KINDS: { [K in DocKind]: KindRow<K> } = {
  map: MAP,
  palette: PALETTE,
  scene: SCENE,
  character: CHARACTER,
  ...ARMOURY_ROWS,
};

/** What every kind shows, text or file: its label, its chip, whether a session may hold a copy. */
export const FACTS: Record<AssetKind, KindFacts> = { ...KINDS, ...FILE_ROWS };

/** The kind chips' order. */
export const KIND_ORDER: readonly AssetKind[] = [
  'map',
  'scene',
  'character',
  'weapon',
  'loadout',
  'effect',
  'area',
  'modifier',
  'module',
  'tactics',
  'alwaysOn',
  'soloMode',
  'handout',
  'image',
  'palette',
];

// The Armoury's claim by their envelope's type, before the character's loose claim by shape.
const TEXT_ORDER: readonly DocKind[] = [
  'map',
  'scene',
  'weapon',
  'effect',
  'area',
  'loadout',
  'modifier',
  'module',
  'tactics',
  'alwaysOn',
  'soloMode',
  'character',
  'palette',
];

/** Which text kind an uploaded file is, from what it says it is (or, for a character, its shape); null for anything else. */
export function kindOfFile(text: string): DocKind | null {
  const data = parse(text);
  return TEXT_ORDER.find((k) => KINDS[k].claims(data)) ?? null;
}

/** What an upload says of the files no row claims: "Not a map, scene, … or palette file: a, b." */
export function refusalSentence(names: readonly string[]): string {
  const words = KIND_ORDER.map((k) => FACTS[k].label.toLowerCase());
  return `Not a ${words.slice(0, -1).join(', ')} or ${words.at(-1)} file: ${names.join(', ')}.`;
}

/** A name a person's file system will take: no separators or reserved marks, never empty. */
export function safeName(name: string): string {
  return name.replace(/[\\/:*?"<>|]+/g, '-').trim() || 'asset';
}

/** A file name from an asset's name. Text kinds are JSON. */
export function fileNameFor(name: string, extension = 'json'): string {
  return `${safeName(name)}.${extension}`;
}

/**
 * The text row for a kind, taken as the general one: each row's encode, decode,
 * fresh and shot agree on their content, which TypeScript cannot see through the
 * union of rows. A file kind has no text row - it is bytes - and throws.
 */
export function textRow(kind: AssetKind): KindRow<DocKind> {
  if (isFileKind(kind)) throw new Error(`${FACTS[kind].label} assets are files, not text.`);
  return KINDS[kind] as unknown as KindRow<DocKind>;
}

/** A version's picture from its stored text, or null when the text does not read. */
export function shotOf(kind: AssetKind, text: string): ThumbShot | null {
  const row = textRow(kind);
  const { content } = row.decode(text);
  return content ? row.shot(content) : null;
}
